import {
  useMutation,
  type UseMutationResult,
} from "@tanstack/react-query";
import { ApiError } from "../client";
import { useApiClient } from "../context";
import { mapProblemDetail } from "../../errors/mapProblemDetail";
import type { UIError } from "../../errors/types";
import type { components } from "../schema";

export type DeductionRequest = components["schemas"]["DeductionRequest"];
export type DeductionResponse = components["schemas"]["DeductionResponse"];

/** useDeduction.mutate({}) の引数。 */
export type DeductionVariables = {
  playlineId: string;
  sceneId: string;
} & DeductionRequest;

/** useDeduction の error は UIError に正規化済み。 */
export type UseDeductionResult = UseMutationResult<
  DeductionResponse,
  UIError,
  DeductionVariables
>;

/**
 * interactive-deduction (Phase 3-3, Task 6.1) 駆け引きラウンド判定 hook。
 *
 * DeductionPanel から `mutate({ playlineId, sceneId, memoryFragmentKey,
 * contradictionId })` を呼び、成功時は `DeductionResponse` ({ success,
 * remainingAttempts, nextSceneId?, forcedBadEnding }) を返す。
 *
 * エラー経路:
 *   - 404 /errors/not-found (endpoint-disabled / contradictionId 不在) → UIError.notFound
 *   - 409 /errors/conflict (NoAttempts / AlreadyResolved) → UIError.conflict
 *   - 400 /errors/bad-request (invalid memoryFragmentKey / body shape) → UIError.badRequest
 * mapProblemDetail が Problem Detail → UIError kind の mapping を担う。
 */
export function useDeduction(): UseDeductionResult {
  const client = useApiClient();

  return useMutation<DeductionResponse, UIError, DeductionVariables>({
    mutationFn: async ({ playlineId, sceneId, ...body }) => {
      try {
        const { data, error } = await client.POST(
          "/playlines/{playlineId}/scenes/{sceneId}/deduction",
          {
            params: { path: { playlineId, sceneId } },
            body,
          },
        );
        if (error !== undefined) {
          throw new ApiError(
            typeof (error as { status?: number }).status === "number"
              ? (error as { status: number }).status
              : 0,
            error as unknown as ApiError["problem"],
          );
        }
        if (data === undefined) {
          throw new ApiError(500, {
            type: "about:blank",
            title: "Empty response",
            status: 500,
            detail: "postDeduction returned no data",
          });
        }
        return data;
      } catch (err: unknown) {
        throw mapProblemDetail(err);
      }
    },
  });
}
